import { useState, useEffect } from 'react';
import { AlertCircle, Trophy, RotateCcw, TrendingUp, Share2, Link as LinkIcon, Flame } from 'lucide-react';
import gameApi from './api/gameApi';
import { isProfane } from './utils/profanityFilter';

const BOARD_WIDTH = 21;
const SEED_ROW = 8;
const SEED_START_COL = 8;

interface PlacedWordData {
  word: string;
  row: number;
  startCol: number;
  baseScore: number;
  finalScore: number;
  matchLength: number;
  streakBonuses: { letter: string; streak: number; bonus: number; column: number; path: string[] }[];
}

interface PositionStreak {
  letter: string;
  streak: number;
}

interface BoardStreaks {
  [position: string]: PositionStreak;
}

// Static Keyboard Component
const StaticKeyboard = ({ onKeyPress, disabled }: { onKeyPress: (key: string) => void; disabled: boolean }) => {
  const rows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK'],
    ['←', '→']
  ];

  const getButtonStyle = (key: string) => {
    if (key === 'ENTER') return 'bg-green-600 hover:bg-green-700 text-white px-2 sm:px-4 text-xs sm:text-sm';
    if (key === 'BACK') return 'bg-red-600 hover:bg-red-700 text-white px-2 sm:px-4 text-xl sm:text-2xl';
    if (key === '←' || key === '→') return 'bg-blue-600 hover:bg-blue-700 text-white flex-1';
    return 'bg-slate-600 hover:bg-slate-700 text-white w-[52px] sm:w-14 md:w-12';
  };

  return (
    <div className="bg-slate-800/95 backdrop-blur-sm border border-white/20 rounded-xl p-2 sm:p-3 mt-3">
      <div className="space-y-2">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-1">
            {row.map((key) => (
              <button
                key={key}
                onClick={() => onKeyPress(key)}
                disabled={disabled}
                className={`${getButtonStyle(key)} h-14 md:h-14 rounded font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                {key === 'BACK' ? '⌫' : key}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const App = () => {
  const [seedWord, setSeedWord] = useState('');
  const [seedDate, setSeedDate] = useState('');
  const [board, setBoard] = useState<(string | null)[][]>([]);
  const [tempLetters, setTempLetters] = useState<{ row: number; col: number; letter: string }[]>([]);
  const [placedWords, setPlacedWords] = useState<PlacedWordData[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [boardStreaks, setBoardStreaks] = useState<BoardStreaks>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [todaysHigh, setTodaysHigh] = useState<number | null>(null);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [scoreSubmitError, setScoreSubmitError] = useState('');
  const [showShareCard, setShowShareCard] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [linkCopySuccess, setLinkCopySuccess] = useState(false);
  
  // Visual feedback state
  const [recentWordRow, setRecentWordRow] = useState<number | null>(null);
  const [showStreakAnimation, setShowStreakAnimation] = useState(false);
  const [streakAnimationData, setStreakAnimationData] = useState<{ letter: string; streak: number; bonus: number; column: number; path: string[] }[]>([]);
  const [highlightedCells, setHighlightedCells] = useState<Set<string>>(new Set());
  const [showInstructions, setShowInstructions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);

  // Helper function to get the active row (only row where user can type)
  const getActiveRow = () => {
    if (placedWords.length === 0) return SEED_ROW; // Start at seed row
    const lowestPlacedRow = Math.min(...placedWords.map(pw => pw.row)); // Lowest array index = highest visual row
    return lowestPlacedRow - 1; // Next row down in array = next row up visually
  };

  const getTodayUTC8 = (): string => {
    const now = new Date();
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    utc.setHours(utc.getHours() - 8);
    return `${utc.getFullYear()}-${String(utc.getMonth() + 1).padStart(2, '0')}-${String(utc.getDate()).padStart(2, '0')}`;
  };

  const getYesterdayUTC8 = (): string => {
    const now = new Date();
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    utc.setHours(utc.getHours() - 8);
    utc.setDate(utc.getDate() - 1);
    return `${utc.getFullYear()}-${String(utc.getMonth() + 1).padStart(2, '0')}-${String(utc.getDate()).padStart(2, '0')}`;
  };

  const getStreakColor = (streak: number): string => {
    if (streak >= 5) return 'text-red-400';
    if (streak >= 4) return 'text-orange-400';
    if (streak >= 3) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const getRank = (score: number): { emoji: string; title: string } => {
    if (score >= 5000) return { emoji: '📜', title: 'Etymologist' };
    if (score >= 4000) return { emoji: '📖', title: 'Librarian' };
    if (score >= 3000) return { emoji: '🍎', title: 'Teacher' };
    if (score >= 2000) return { emoji: '🎓', title: 'Graduate' };
    if (score >= 1000) return { emoji: '📚', title: 'Learner' };
    return { emoji: '🌱', title: 'Newcomer' };
  };

  useEffect(() => {
    initializeGame();
    loadStreak();
  }, []);

  const loadStreak = () => {
    const savedStreak = localStorage.getItem('wordupmanship_streak');
    const lastPlayDate = localStorage.getItem('wordupmanship_last_play');
    const today = getTodayUTC8();
    const yesterday = getYesterdayUTC8();

    if (!lastPlayDate || !savedStreak) {
      setCurrentStreak(0);
      return;
    }

    if (lastPlayDate === yesterday) {
      setCurrentStreak(parseInt(savedStreak));
    } else if (lastPlayDate === today) {
      setCurrentStreak(parseInt(savedStreak));
    } else {
      setCurrentStreak(0);
      localStorage.removeItem('wordupmanship_streak');
      localStorage.removeItem('wordupmanship_last_play');
    }
  };

  const initializeGame = async () => {
    setIsInitializing(true);
    setError('');
    
    try {
      const game = await gameApi.createGame();
      setSeedWord(game.seedWord);
      setSeedDate(getTodayUTC8());
      setBoard(game.board);
      
      // Initialize position streaks for seed word
      const initialStreaks: BoardStreaks = {};
      game.seedWord.split('').forEach((letter: string, i: number) => {
        const pos = `${SEED_ROW}-${SEED_START_COL + i}`;
        initialStreaks[pos] = { letter, streak: 1 };
      });
      setBoardStreaks(initialStreaks);
      
      setPlacedWords([{
        word: game.seedWord,
        row: SEED_ROW,
        startCol: SEED_START_COL,
        baseScore: 0,
        finalScore: 0,
        matchLength: 0,
        streakBonuses: []
      }]);
      
      try {
        const high = await gameApi.getTodaysHigh();
        setTodaysHigh(high);
      } catch (error) {
        console.error('Failed to fetch today\'s high:', error);
        setTodaysHigh(null);
      }
      
      const centerCol = SEED_START_COL + Math.floor(game.seedWord.length / 2);
      setSelectedCell({ row: SEED_ROW - 1, col: centerCol }); // Row above seed (array index 7 = display row 2)
    } catch (error) {
      console.error('Failed to initialize game:', error);
      setError('Failed to connect to game server. Please check your connection and try again.');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleStaticKeyPress = (key: string) => {
    if (!selectedCell || isLoading) return;
    
    const activeRow = getActiveRow();

    if (key === 'ENTER') {
      // Only allow ENTER on active row
      if (selectedCell.row !== activeRow) return;
      submitWord();
    } else if (key === 'BACK' || key === '⌫') {
      const hasLetterHere = tempLetters.find(t => t.row === selectedCell.row && t.col === selectedCell.col);
      if (hasLetterHere) {
        removeLetter(selectedCell.row, selectedCell.col);
      } else if (selectedCell.col > 0) {
        setSelectedCell({ row: selectedCell.row, col: selectedCell.col - 1 });
      }
    } else if (key === '←') {
      if (selectedCell.col > 0) {
        setSelectedCell({ row: selectedCell.row, col: selectedCell.col - 1 });
      }
    } else if (key === '→') {
      if (selectedCell.col < BOARD_WIDTH - 1) {
        setSelectedCell({ row: selectedCell.row, col: selectedCell.col + 1 });
      }
    } else if (/^[A-Z]$/.test(key)) {
      // Only allow letter typing on active row
      if (selectedCell.row !== activeRow) return;
      addLetter(selectedCell.row, selectedCell.col, key);
      if (selectedCell.col < BOARD_WIDTH - 1) {
        setSelectedCell({ row: selectedCell.row, col: selectedCell.col + 1 });
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedCell || isLoading) return;
      const key = e.key.toUpperCase();
      
      if (/^[A-Z]$/.test(key)) {
        e.preventDefault();
        handleStaticKeyPress(key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleStaticKeyPress('BACK');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleStaticKeyPress('ENTER');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleStaticKeyPress('←');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleStaticKeyPress('→');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearTempLetters();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedCell, tempLetters, isLoading]);

  // Auto-scroll active CELL into view on mobile (cursor-following)
  useEffect(() => {
    if (!selectedCell) return;
    
    // Only on mobile (768px or less)
    if (window.innerWidth > 768) return;
    
    // Find the active cell element by row AND column
    const cellElement = document.querySelector(`[data-row="${selectedCell.row}"][data-col="${selectedCell.col}"]`);
    if (cellElement) {
      cellElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [selectedCell?.row, selectedCell?.col]); // ← Trigger on BOTH row and column changes

  const handleCellClick = (row: number, col: number) => {
    if (isLoading) return;
    
    const activeRow = getActiveRow();
    
    // Only allow clicks on the active row
    if (row !== activeRow) return;
    
    // Don't allow clicks on cells that already have placed letters
    if (board[row][col]) return;
    
    setSelectedCell({ row, col });
    setError('');
  };

  const addLetter = (row: number, col: number, letter: string) => {
    setError('');
    const existing = tempLetters.find(t => t.row === row && t.col === col);
    if (existing) {
      setTempLetters(tempLetters.map(t => t.row === row && t.col === col ? { ...t, letter } : t));
    } else {
      setTempLetters([...tempLetters, { row, col, letter }]);
    }
  };

  const removeLetter = (row: number, col: number) => {
    setError('');
    setTempLetters(tempLetters.filter(t => !(t.row === row && t.col === col)));
  };

  const clearTempLetters = () => {
    setTempLetters([]);
    setError('');
  };

  // Calculate score with position-based streaks (client-side for visual feedback)
  const calculateLocalScore = (word: string, row: number, startCol: number, matchLength: number): {
    baseScore: number;
    finalScore: number;
    newStreaks: BoardStreaks;
    streakBonuses: { letter: string; streak: number; bonus: number; column: number; path: string[] }[];
  } => {
    const baseScore = word.length + matchLength;
    const newStreaks: BoardStreaks = { ...boardStreaks };
    
    const positionStreaks: { letter: string; streak: number; column: number; path: string[] }[] = [];
    
    word.split('').forEach((letter, i) => {
      const col = startCol + i;
      const pos = `${row}-${col}`;
      const prevPos = `${row + 1}-${col}`;
      
      const prevStreak = newStreaks[prevPos];
      
      if (prevStreak && prevStreak.letter === letter) {
        const newStreakValue = prevStreak.streak + 1;
        newStreaks[pos] = { letter, streak: newStreakValue };
        
        // Build path from bottom to top for this streak
        const path: string[] = [];
        let currentRow = row;
        while (currentRow <= SEED_ROW) {
          const checkPos = `${currentRow}-${col}`;
          if (newStreaks[checkPos]?.letter === letter) {
            path.push(checkPos);
            currentRow++;
          } else {
            break;
          }
        }
        path.reverse(); // Bottom to top order
        
        positionStreaks.push({ letter, streak: newStreakValue, column: col, path });
      } else {
        newStreaks[pos] = { letter, streak: 1 };
        positionStreaks.push({ letter, streak: 1, column: col, path: [pos] });
      }
    });
    
    const streakBonuses: { letter: string; streak: number; bonus: number; column: number; path: string[] }[] = [];
    let finalScore = 0;
    
    positionStreaks.forEach(({ letter, streak, column, path }) => {
      if (streak >= 3) {
        const bonus = baseScore * streak;
        streakBonuses.push({ letter, streak, bonus, column, path });
        finalScore += bonus;
      }
    });
    
    if (streakBonuses.length === 0) {
      finalScore = baseScore;
    }
    
    return { baseScore, finalScore, newStreaks, streakBonuses };
  };

  const submitWord = async () => {
    if (tempLetters.length === 0) {
      setError('Type some letters first');
      return;
    }

    const sorted = [...tempLetters].sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });

    const rows = new Set(sorted.map(t => t.row));
    if (rows.size > 1) {
      setError('All letters must be in the same row');
      return;
    }

    const row = sorted[0].row;
    const cols = sorted.map(t => t.col);
    for (let i = 1; i < cols.length; i++) {
      if (cols[i] !== cols[i-1] + 1) {
        setError('Letters must be contiguous (side by side)');
        return;
      }
    }

    const word = sorted.map(t => t.letter).join('');
    if (word.length < 2) {
      setError('Word must be at least 2 letters');
      return;
    }

    // Check for profanity (client-side UX)
    if (isProfane(word)) {
      setError('Please use family-friendly words');
      return;
    }

    const startCol = sorted[0].col; // Column where user started typing

    setIsLoading(true);
    setError('');

    try {
      const validation = await gameApi.validateWord({ 
        word: word.toUpperCase(), 
        row,
        startCol,
        board, 
        usedWords: placedWords.map(pw => pw.word) 
      });
      
      if (!validation.placements || validation.placements.length === 0) {
        setError('No valid placement found');
        setIsLoading(false);
        return;
      }

      const bestPlacement = validation.placements[0];
      // Use startCol from user input (line 422), not from backend placement

      // Calculate local score for visual feedback
      const localScoreData = calculateLocalScore(word.toUpperCase(), row, startCol, bestPlacement.matchLength);

      // Update board
      const newBoard = board.map(r => [...r]);
      for (let i = 0; i < word.length; i++) {
        newBoard[row][startCol + i] = word[i].toUpperCase();
      }

      setBoard(newBoard);
      setBoardStreaks(localScoreData.newStreaks);
      setTotalScore(totalScore + localScoreData.finalScore);
      setPlacedWords([...placedWords, {
        word: word.toUpperCase(),
        row,
        startCol,
        baseScore: localScoreData.baseScore,
        finalScore: localScoreData.finalScore,
        matchLength: bestPlacement.matchLength,
        streakBonuses: localScoreData.streakBonuses
      }]);
      setTempLetters([]);

      // Visual feedback
      setRecentWordRow(row);
      setTimeout(() => setRecentWordRow(null), 1000);
      
      // Show streak animation if bonuses exist
      if (localScoreData.streakBonuses.length > 0) {
        const allHighlights = new Set<string>();
        
        localScoreData.streakBonuses.forEach((bonus, bonusIdx) => {
          bonus.path.forEach((cellPos, pathIdx) => {
            setTimeout(() => {
              setHighlightedCells(prev => new Set([...prev, cellPos]));
            }, bonusIdx * 200 + pathIdx * 100);
          });
          bonus.path.forEach(pos => allHighlights.add(pos));
        });
        
        const totalDelay = localScoreData.streakBonuses.length * 200 + Math.max(...localScoreData.streakBonuses.map(b => b.path.length)) * 100;
        
        setTimeout(() => {
          setStreakAnimationData(localScoreData.streakBonuses);
          setShowStreakAnimation(true);
          setHighlightedCells(new Set());
          setTimeout(() => setShowStreakAnimation(false), 2500);
        }, totalDelay + 200);
      }

      if (placedWords.length >= 8) {
        if (!scoreSubmitted) {
          try {
            await gameApi.submitScore({ seedWord, seedDate, score: totalScore + localScoreData.finalScore });
            setScoreSubmitted(true);
            setScoreSubmitError('');
            
            // Update streak
            const today = getTodayUTC8();
            const lastPlayDate = localStorage.getItem('wordupmanship_last_play');
            const yesterday = getYesterdayUTC8();
            let newStreak = 1;

            if (lastPlayDate === yesterday) {
              newStreak = currentStreak + 1;
            } else if (lastPlayDate === today) {
              newStreak = currentStreak;
            }

            setCurrentStreak(newStreak);
            localStorage.setItem('wordupmanship_streak', newStreak.toString());
            localStorage.setItem('wordupmanship_last_play', today);
          } catch (error) {
            console.error('Failed to submit score:', error);
            setScoreSubmitError('Score could not be saved today');
          }
        }
        setShowShareCard(true);
      } else {
        if (row > 0) {
          const centerCol = startCol + Math.floor(word.length / 2);
          setSelectedCell({ row: row - 1, col: centerCol });
        }
      }

      setSuccess(`+${localScoreData.finalScore} points! ${localScoreData.streakBonuses.length > 0 ? '🔥 Streak bonus!' : ''}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Failed to submit word:', error);
      setError(error.message || 'Failed to submit word. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetGame = () => {
    setTotalScore(0);
    setPlacedWords([]);
    setTempLetters([]);
    setError('');
    setSuccess('');
    setBoardStreaks({});
    setSelectedCell(null);
    setShowShareCard(false);
    setScoreSubmitted(false);
    setScoreSubmitError('');
    setHighlightedCells(new Set());
    setRecentWordRow(null);
    setShowStreakAnimation(false);
    initializeGame();
  };

  const generateShareText = () => {
    const today = getTodayUTC8();
    const formattedDate = (() => {
      const [year, month, day] = today.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    })();
    const isNewHigh = todaysHigh === null || totalScore > todaysHigh;
    const highScoreText = isNewHigh ? `${totalScore} 🏆 (NEW!)` : `${todaysHigh} 🏆`;
    const streakText = currentStreak > 0 ? `\nCurrent Streak: ${currentStreak} 🔥` : '';
    return `WordUpmanship ${formattedDate}\n\nStarting Word: ${seedWord}\nYour Score: ${totalScore} 🎯${streakText}\nToday's High: ${highScoreText}\n\nCan you top my score?\n${window.location.origin}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareText());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      setError('Failed to copy to clipboard');
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setLinkCopySuccess(true);
      setTimeout(() => setLinkCopySuccess(false), 2000);
    } catch (error) {
      setError('Failed to copy link');
    }
  };

  const getTempLetter = (row: number, col: number) => {
    return tempLetters.find(t => t.row === row && t.col === col)?.letter;
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading WordUpmanship...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-2 md:p-4 overflow-x-hidden">
      <div className="max-w-6xl mx-auto w-full">
        <div className="text-center mb-6">
          {/* WordUpmanship row - blue/cyan gradient tiles */}
          <div className="flex justify-center gap-1 sm:gap-2 mb-3">
            {/* First tile: Logo with transparent background */}
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded flex items-center justify-center shadow-lg p-1">
              <img src="/logo-transparent.png" alt="W" className="w-full h-full object-contain" />
            </div>
            
            {/* Remaining letters */}
            {['o','r','d','U','p','m','a','n','s','h','i','p'].map((letter, i) => (
              <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded flex items-center justify-center shadow-lg">
                <span className="text-white text-base sm:text-xl md:text-3xl font-black" style={{fontFamily: '"Montserrat", "Nunito", "Quicksand", system-ui, sans-serif', letterSpacing: '-0.02em'}}>{letter}</span>
              </div>
            ))}
          </div>
          
          <p className="text-purple-300">Click tiles • Use keyboard below • ← → to move • ENTER to submit</p>
        </div>

        <div className="grid gap-4 mb-6 grid-cols-1">
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4 shadow-xl max-w-md mx-auto w-full">
            <div className="flex items-center gap-2 justify-center">
              <Trophy className="text-yellow-300" size={32} />
              <div>
                <div className="text-purple-100 text-sm font-medium">Total Score</div>
                <div className="text-3xl font-bold text-white mt-1">{totalScore}</div>
              </div>
            </div>
          </div>
        </div>

        {(error || success) && (
          <div className={`flex items-center gap-2 p-4 mb-4 rounded-lg ${error ? 'bg-red-500/20 border border-red-500/50 text-red-200' : 'bg-green-500/20 border border-green-500/50 text-green-200'}`}>
            <AlertCircle size={20} />
            <span>{error || success}</span>
          </div>
        )}

        {/* Streak Bonus Animation */}
        {showStreakAnimation && (
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
            <div className="relative">
              {streakAnimationData.map((bonus, idx) => (
                <div 
                  key={idx}
                  className="absolute animate-float-up opacity-0"
                  style={{
                    left: `${idx * 60 - (streakAnimationData.length * 30)}px`,
                    animationDelay: `${idx * 0.15}s`,
                    animationDuration: '2s',
                    animationFillMode: 'forwards'
                  }}
                >
                  <div className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 px-4 py-2 rounded-full shadow-lg border-2 border-yellow-400">
                    <Flame size={20} className="text-yellow-300" />
                    <span className="text-white font-bold text-xl">×{bonus.streak}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 w-full">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-1 sm:p-2 md:p-4 border border-white/20 mb-4 game-board-container">
              <div className="flex justify-center max-h-[65vh] md:max-h-none overflow-y-auto overflow-x-auto md:overflow-visible">
                <div className="inline-block">
                  {board.map((row, rowIndex) => {
                    const displayRowNumber = 9 - rowIndex;
                    const isRecentWord = recentWordRow === rowIndex;
                    return (
                      <div 
                        key={rowIndex} 
                        data-row={rowIndex}
                        className={`flex gap-0.5 mb-0.5 transition-all ${rowIndex < SEED_ROW ? 'cursor-pointer hover:bg-white/5' : ''} ${isRecentWord ? 'scale-105 bg-white/10' : ''}`}
                      >
                        <div className="w-8 text-purple-300 text-xs flex items-center justify-center font-mono">{displayRowNumber}</div>
                        {row.map((cell, colIndex) => {
                          const tempLetter = getTempLetter(rowIndex, colIndex);
                          const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                          const cellPos = `${rowIndex}-${colIndex}`;
                          const isHighlighted = highlightedCells.has(cellPos);
                          return (
                            <div 
                              key={colIndex} 
                              data-row={rowIndex}
                              data-col={colIndex}
                              onClick={() => handleCellClick(rowIndex, colIndex)} 
                              className={`w-8 h-8 border flex items-center justify-center font-bold text-sm transition-all ${cell ? rowIndex === SEED_ROW ? 'bg-yellow-500 text-yellow-900 border-yellow-600 shadow-lg' : 'bg-purple-500 text-white border-purple-600' : tempLetter ? 'bg-blue-500/70 text-white border-blue-400 cursor-pointer animate-pulse' : rowIndex < SEED_ROW ? 'bg-white/5 border-white/20 hover:bg-white/10 cursor-pointer' : 'bg-white/5 border-white/20'} ${isSelected ? 'ring-2 ring-cyan-400 ring-offset-1 ring-offset-slate-900' : ''} ${isHighlighted ? 'bg-orange-500 scale-110 shadow-xl shadow-orange-500/50 animate-pulse z-10' : ''}`}
                            >
                              {cell || tempLetter}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
              <StaticKeyboard onKeyPress={handleStaticKeyPress} disabled={isLoading} />
            </div>
            
            {/* New Game Button - between board and Word History */}
            <div className="text-center mt-4">
              <button onClick={resetGame} disabled={isLoading} className="px-6 py-3 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 mx-auto">
                <RotateCcw size={18} />New Game
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Trophy size={20} className="text-yellow-400" />
                Word History
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {placedWords.slice().reverse().map((pw, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${idx === 0 ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50' : 'bg-white/5'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-white font-bold text-lg">{pw.word}</span>
                      <span className="text-green-400 font-bold text-lg">+{pw.finalScore}</span>
                    </div>
                    {pw.word !== seedWord && (
                      <div className="text-xs space-y-1">
                        <div className="text-purple-300">Base: {pw.word.length} + {pw.matchLength} match = {pw.baseScore}</div>
                        {pw.streakBonuses.length > 0 ? (
                          <div>
                            <div className="text-purple-300 mb-1">Streak bonuses:</div>
                            <div className="flex flex-wrap gap-1">
                              {pw.streakBonuses.map((sb, i) => (
                                <span key={i} className={`${getStreakColor(sb.streak)} flex items-center gap-1 text-xs`}>
                                  <Flame size={12} />
                                  {sb.letter}×{sb.streak} (={sb.bonus})
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-400 text-xs">No streak bonuses (all &lt; 3)</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

{/* Collapsible Stats */}
<div className="mt-6">
  <button 
    onClick={() => setShowStats(!showStats)}
    className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/20 text-white font-semibold transition-all flex items-center justify-between"
  >
    <span className="text-lg">Game Stats</span>
    <span className="text-2xl">{showStats ? '−' : '+'}</span>
  </button>
  
  {showStats && (
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 mt-2">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-4 shadow-xl">
          <div className="flex items-center gap-2">
            <TrendingUp className="text-green-300" size={32} />
            <div>
              <div className="text-blue-100 text-sm">Words Placed</div>
              <div className="text-3xl font-bold text-white mt-1">{placedWords.length - 1}/8</div>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
          <div className="text-purple-200 text-sm mb-2">Position Streaks (≥3)</div>
          <div className="space-y-1 max-h-20 overflow-y-auto text-xs">
            {Object.entries(boardStreaks)
              .filter(([_, data]) => data.streak >= 3)
              .sort((a, b) => b[1].streak - a[1].streak)
              .map(([pos, data]) => {
                return (
                  <div key={pos} className="flex items-center justify-between gap-2 bg-white/5 px-2 py-1 rounded">
                    <div className="flex items-center gap-1">
                      <Flame className={getStreakColor(data.streak)} size={12} />
                      <span className="text-white font-bold">{data.letter}</span>
                    </div>
                    <span className={`${getStreakColor(data.streak)}`}>×{data.streak}</span>
                  </div>
                );
              })}
            {Object.values(boardStreaks).every(d => d.streak < 3) && (
              <span className="text-gray-400">Build 3+ position streaks for bonuses</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )}
</div>

{/* Collapsible Instructions */}
<div className="mt-6 mb-8">
  <button 
    onClick={() => setShowInstructions(!showInstructions)}
    className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/20 text-white font-semibold transition-all flex items-center justify-between"
  >
    <span className="text-lg">How to Play</span>
    <span className="text-2xl">{showInstructions ? '−' : '+'}</span>
  </button>
  
  {showInstructions && (
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 mt-2 text-purple-200 space-y-3">
      <div className="space-y-2">
        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">1.</strong> Note the yellow starting word on the bottom row
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">2.</strong> Type a new word <strong>left-to-right on a single row</strong> above it
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">3.</strong> Each word entered must <strong>reuse letters from the word directly below</strong> — at least one column must line up letter for letter, and any overlapping letters must match
        </div>
{/* Visual Example */}
<div className="bg-white/5 p-3 rounded text-sm">
  <div className="mb-2 text-purple-300">
    <strong>Example:</strong> If your starting word is BLAST, you could stack ASTEROID on top
  </div>
  <div className="flex flex-col items-center space-y-1 font-mono">
    {/* Row 1 (ASTEROID at positions 2–9) */}
    <div className="flex space-x-1">
      <div className="w-6 h-6" />
      <div className="w-6 h-6" />

      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">A</div>
      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">S</div>
      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">T</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">E</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">R</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">O</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">I</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">D</div>
    </div>

    {/* Row 0 (BLAST at positions 0–4) */}
    <div className="flex space-x-1">
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">B</div>
      <div className="w-6 h-6 flex items-center justify-center bg-white/10 rounded">L</div>
      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">A</div>
      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">S</div>
      <div className="w-6 h-6 flex items-center justify-center bg-green-500/30 rounded">T</div>

      <div className="w-6 h-6" />
      <div className="w-6 h-6" />
      <div className="w-6 h-6" />
      <div className="w-6 h-6" />
      <div className="w-6 h-6" />
    </div>
  </div>

  <div className="mt-2 text-purple-300">
    This word works because <strong>AST</strong> aligns perfectly.  
    Any misaligned letter would make the word invalid.
  </div>
</div>




        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">4.</strong> Words stack upward, but never run vertically
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">5.</strong> Use arrow keys (← →) or click tiles to move horizontally
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">6.</strong> Press ENTER to submit your word
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">7.</strong> Build vertical letter streaks (3+ in the same column) to earn multiplier bonuses
        </div>

        <div className="bg-white/5 p-3 rounded">
          <strong className="text-white">8.</strong> Place 8 words to complete the game
        </div>
      </div>

      <div className="mt-4 p-4 bg-purple-500/20 rounded border border-purple-400/50">
        <strong className="text-yellow-300">Scoring:</strong>
        <div className="mt-2 text-sm">
          Base = word length + matching letters
          <br />
          Bonus = base × streak count (for each 3+ vertical streak)
          <br />
          Final = base + all bonuses
        </div>
      </div>

      <div className="mt-4 p-4 bg-purple-500/20 rounded border border-purple-400/50">
        <strong className="text-yellow-300">Daily Ranks:</strong>
        <div className="mt-2 text-sm space-y-1">
          <div>🌱 Newcomer: 0-999</div>
          <div>📚 Learner: 1,000-1,999</div>
          <div>🎓 Graduate: 2,000-2,999</div>
          <div>🍎 Teacher: 3,000-3,999</div>
          <div>📖 Librarian: 4,000-4,999</div>
          <div>📜 Etymologist: 5,000+</div>
        </div>
      </div>
    </div>
  )}
</div>



        {showShareCard && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-2xl p-8 max-w-md w-full border-4 border-yellow-400 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center justify-center gap-2 whitespace-nowrap"><Share2 size={28} />Share Today's Game</h2>
                {scoreSubmitError && (<div className="mb-4 p-3 bg-orange-500/20 border border-orange-500/50 rounded-lg text-orange-200 text-sm">⚠️ {scoreSubmitError}</div>)}
                <div className="bg-slate-800 rounded-xl p-6 mb-6 border-2 border-purple-400">
                  <div className="text-left space-y-2 font-mono text-sm">
                    <div className="text-white font-bold text-lg">WordUpmanship {(() => {
                      const today = getTodayUTC8();
                      const [year, month, day] = today.split('-');
                      return `${parseInt(month)}/${parseInt(day)}/${year}`;
                    })()}</div>
                    <div className="text-purple-300">Starting Word: <span className="text-yellow-300 font-bold">{seedWord}</span></div>
                    <div className="text-purple-300">Your Score: <span className="text-yellow-300 font-bold">{totalScore} {getRank(totalScore).title === 'Graduate' ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-xs">🎓</span> : getRank(totalScore).emoji} {getRank(totalScore).title}</span></div>
                    {currentStreak > 0 && (
                      <div className="text-purple-300">Current Streak: <span className="text-yellow-300 font-bold">{currentStreak} 🔥</span></div>
                    )}
                    <div className="text-purple-300">
                      Today's High: <span className="text-yellow-300 font-bold">
                        {todaysHigh === null || totalScore > todaysHigh 
                          ? `${totalScore} 🏆 (NEW!)` 
                          : `${todaysHigh} 🏆`
                        }
                      </span>
                    </div>
                    <div className="text-purple-400 mt-4 text-xs">Can you top my score?<br/>{window.location.origin}</div>
                  </div>
                </div>
                {copySuccess && (<div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 font-semibold">✓ Score copied to clipboard!</div>)}
                {linkCopySuccess && (<div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 font-semibold">✓ Link copied to clipboard!</div>)}
                <div className="space-y-3 mb-4">
                  <button onClick={copyToClipboard} className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-bold transition-all shadow-lg flex items-center justify-center gap-2"><Share2 size={20} />Copy Score & Link</button>
                  <button onClick={copyLinkToClipboard} className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-bold transition-all shadow-lg flex items-center justify-center gap-2"><LinkIcon size={20} />Copy Link Only</button>
                </div>
                <button onClick={() => setShowShareCard(false)} className="w-full px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-all">Close</button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes float-up {
            0% {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
            50% {
              transform: translateY(-30px) scale(1.1);
              opacity: 1;
            }
            100% {
              transform: translateY(-80px) scale(0.8);
              opacity: 0;
            }
          }
          .animate-float-up {
            animation: float-up 2s ease-out forwards;
          }
        `}</style>

        <footer className="py-2 text-center text-xs text-white/50">
  © {new Date().getFullYear()} WordUpmanship
</footer>

      </div>
    </div>
  );
};

export default App;
